import React, { useId } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { type CompareContrastData } from "@/types/models/Exportables.interface";
import { ORGANIZER_TOPIC_MAX_LENGTH } from "./CompareContrastOrganizer.constants";
import {
  bothLabelCss,
  bothSectionContainerCss,
  bottomLineInputCss,
  bottomLineInputWrapperCss,
  compareContrastContainerCss,
  inputsRowCss,
  paragraphCss,
  vennDiagramTextAreaContainerCss,
} from "./CompareContrastOrganizer.styles";
import OrganizerCircleTextArea from "./OrganizerCircleTextArea";
import { updateTopic } from "./utils";

const CompareContrastOrganizer = ({
  formData,
  onChange,
  title = "Compare and Contrast",
  isStudent = false,
}: TemplateProps<CompareContrastData>) => {
  const leftTopicId = useId();
  const rightTopicId = useId();
  const data = formData as CompareContrastData;
  const leftTopic = data?.topic_pairs?.[0]?.[0] || "";
  const rightTopic = data?.topic_pairs?.[0]?.[1] || "";
  const leftSection = data?.topic_pairs?.[0]?.[2] || "";
  const rightSection = data?.topic_pairs?.[0]?.[3] || "";
  const centerSection = data?.topic_pairs?.[0]?.[4] || "";

  const handler = (
    section:
      | "left-topic"
      | "right-topic"
      | "left-section"
      | "right-section"
      | "center-section",
  ) => {
    return (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      updateTopic(section, e.target.value, onChange, formData);
  };

  const renderTopicsSection = () => (
    <div css={bothSectionContainerCss}>
      <div css={inputsRowCss}>
        <TemplateTextArea
          value={leftTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
          maxLength={ORGANIZER_TOPIC_MAX_LENGTH}
          readOnly={isStudent}
          css={bottomLineInputWrapperCss}
        >
          <TemplateTextArea.Input
            id={leftTopicId}
            data-qa-selector="organizer-left-topic"
            onChange={handler("left-topic")}
            aria-label="Left Topic"
            placeholder="Left Topic"
            css={bottomLineInputCss}
          />
          <TemplateTextArea.Paragraph css={paragraphCss} />
          <TemplateTextArea.CharacterCount />
        </TemplateTextArea>
        <div css={bothLabelCss}>Both</div>
        <TemplateTextArea
          value={rightTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
          maxLength={ORGANIZER_TOPIC_MAX_LENGTH}
          readOnly={isStudent}
          css={bottomLineInputWrapperCss}
        >
          <TemplateTextArea.Input
            id={rightTopicId}
            data-qa-selector="organizer-right-topic"
            onChange={handler("right-topic")}
            aria-label="Right Topic"
            placeholder="Right Topic"
            css={bottomLineInputCss}
          />
          <TemplateTextArea.Paragraph css={paragraphCss} />
          <TemplateTextArea.CharacterCount />
        </TemplateTextArea>
      </div>
    </div>
  );

  const renderVennDiagramSection = () => {
    return (
      <div css={vennDiagramTextAreaContainerCss}>
        <OrganizerCircleTextArea
          section="left"
          value={leftSection}
          onChange={handler("left-section")}
        />
        <OrganizerCircleTextArea
          section="center"
          value={centerSection}
          onChange={handler("center-section")}
        />
        <OrganizerCircleTextArea
          section="right"
          value={rightSection}
          onChange={handler("right-section")}
        />
      </div>
    );
  };

  const renderHeader = () => (
    <ExportablesTemplateHeaderSection
      templateTitle={title}
      articleTitle={formData?.article_title}
    />
  );

  return (
    <div
      css={compareContrastContainerCss}
      aria-label="Compare Contrast Organizer"
    >
      {renderHeader()}
      {renderTopicsSection()}
      {renderVennDiagramSection()}
    </div>
  );
};

export default CompareContrastOrganizer;
